// Per-page SEO surface. Renders into <head> via react-helmet-async.
//
// Crawlers that execute JavaScript (Google, Bing) will see the
// title/description/canonical/og:* set here. Crawlers that don't
// (most social previews, smaller engines) fall back to the defaults
// baked into ``index.html``. We intentionally keep both layers.
//
// Always pass ``path`` as the route the user is on (e.g. ``/help``).
// The component derives the canonical + og:url from it.
//
// <Seo
//   title="Help"
//   description="The five questions writers ask most often..."
//   path="/help"
//   image="https://plumescript.ca/help-hero.png"   // optional
//   type="website"                                 // or "article" / "book"
//   noindex                                        // set on private pages
// />

import { Helmet } from "react-helmet-async";
import { useI18n } from "../i18n/I18nContext";

const SITE_NAME = "Plumescript";
const SITE_URL = "https://plumescript.ca";
const DEFAULT_IMAGE = `${SITE_URL}/plumescript-tab-logo.png`;
const DEFAULT_DESCRIPTION =
  "A focused space to write, share, and read in-progress stories. Edit alone or with collaborators in real time, publish chapters as you finish them, and explore what the rest of the community is working on.";

const LOCALE_OG = {
  en: "en_US",
  fr: "fr_CA",
};

function abs(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image,
  type = "website",
  noindex = false,
  // Optional structured-data block (Schema.org JSON-LD object). When
  // passed, it's stringified into a <script type="application/ld+json">
  // tag — useful for Article / Book / Person markup on detail pages.
  jsonLd,
}) {
  const { locale } = useI18n();
  const fullTitle = title ? `${title} — ${SITE_NAME}` : SITE_NAME;
  const url = abs(path) || `${SITE_URL}/`;
  const ogImage = abs(image) || DEFAULT_IMAGE;
  const ogLocale = LOCALE_OG[locale] || LOCALE_OG.en;

  return (
    <Helmet prioritizeSeoTags>
      <html lang={locale || "en"} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={ogLocale} />
      {Object.entries(LOCALE_OG)
        .filter(([k]) => k !== (locale || "en"))
        .map(([, v]) => (
          <meta key={v} property="og:locale:alternate" content={v} />
        ))}
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content={image ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
    </Helmet>
  );
}
